import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, X, Loader2, FileText, Image as ImageIcon, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { chunkedUpload } from "@/lib/chunkedUpload";
import { apiErr } from "@/lib/api";
import { isImage } from "@/components/files/FilePreviewSheet";

const MAX_BYTES = 50 * 1024 * 1024;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv";

// Reusable receipt / invoice attachment control for a fee (used on add + edit forms).
export function FeeAttachments({ attachments = [], onChange, projectId, onPreview }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(0);

  const pick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    for (const file of files) {
      if (file.size > MAX_BYTES) { toast.error(`"${file.name}" is over 50 MB`); continue; }
      setBusy((b) => b + 1);
      try {
        const data = await chunkedUpload({ base: "/storage", file, initBody: { project_id: projectId } });
        const att = {
          file_id: data.file_id, storage_path: data.storage_path,
          original_filename: data.original_filename || file.name,
          content_type: data.content_type || file.type, size: data.size || file.size, label: "",
        };
        onChange((prev) => [...prev, att]);
      } catch (err) {
        toast.error(`"${file.name}": ${apiErr(err, "upload failed")}`);
      } finally {
        setBusy((b) => b - 1);
      }
    }
  };

  const remove = (fid) => onChange((prev) => prev.filter((a) => a.file_id !== fid));
  const setLabel = (fid, label) => onChange((prev) => prev.map((a) => (a.file_id === fid ? { ...a, label } : a)));

  return (
    <div data-testid="fee-attachments">
      <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={onFiles} data-testid="fee-attachment-input" />
      <button type="button" onClick={pick} disabled={busy > 0}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-dashed border-zinc-300 text-xs font-semibold text-zinc-600 hover:bg-zinc-50 hover:border-zinc-400 transition-colors disabled:opacity-50"
        data-testid="fee-attachment-add">
        {busy > 0 ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
        {busy > 0 ? "Uploading…" : "Attach invoice / receipt"}
      </button>

      {attachments.length > 0 && (
        <ul className="mt-2 space-y-1.5" data-testid="fee-attachment-list">
          {attachments.map((a) => {
            const Icon = isImage(a) ? ImageIcon : FileText;
            return (
              <li key={a.file_id} className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-sm px-2 py-1.5" data-testid={`fee-attachment-${a.file_id}`}>
                <Icon className="h-4 w-4 text-zinc-400 shrink-0" />
                <span className="text-xs text-zinc-700 truncate max-w-[160px]" title={a.original_filename}>{a.original_filename}</span>
                <Input value={a.label || ""} onChange={(e) => setLabel(a.file_id, e.target.value)} placeholder="label (e.g. Council receipt)" className="h-7 text-xs rounded-sm flex-1 min-w-[120px]" data-testid={`fee-attachment-label-${a.file_id}`} />
                {onPreview && (
                  <button type="button" onClick={() => onPreview(a)} className="text-zinc-400 hover:text-zinc-900 p-1" title="Preview" data-testid={`fee-attachment-preview-${a.file_id}`}>
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                )}
                <button type="button" onClick={() => remove(a.file_id)} className="text-zinc-400 hover:text-red-600 p-1" title="Remove" data-testid={`fee-attachment-remove-${a.file_id}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
